import { useCallback, useEffect, useRef, useState } from 'react';
import { NgoaiTe, NgoaiTeInput, NgoaiTeResponse } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseNgoaiTeReturn {
  currencies: NgoaiTe[];
  isLoading: boolean;
  addCurrency: (newCurrency: NgoaiTeInput) => Promise<NgoaiTe>;
  updateCurrency: (uuid: string, updatedCurrency: NgoaiTeInput) => Promise<NgoaiTe>;
  deleteCurrency: (uuid: string) => Promise<void>;
  refreshCurrencies: () => Promise<void>;
  getCurrencyById: (uuid: string) => Promise<NgoaiTe | null>;
  getCurrencyByCode: (code: string) => Promise<NgoaiTe | null>;
}

/**
 * Hook for managing Currency (NgoaiTe) data
 *
 * This hook provides functions to fetch, create, update, and delete currencies.
 */
export const useNgoaiTe = (initialCurrencies: NgoaiTe[] = []): UseNgoaiTeReturn => {
  const [currencies, setCurrencies] = useState<NgoaiTe[]>(initialCurrencies);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isFetchingRef = useRef(false);

  const { entity } = useAuth();

  /**
   * Fetch all currencies
   */
  const fetchCurrencies = useCallback(async (): Promise<void> => {
    if (!entity?.slug || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const response = await api.get<NgoaiTeResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/`);
      setCurrencies(response.data.results || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [entity?.slug]);

  /**
   * Helper function to prepare data before sending to API
   */
  function prepareDataForApi(data: any): NgoaiTeInput {
    return {
      ma_nt: data.ma_nt,
      ten_nt: data.ten_nt,
      ten_nt2: data.ten_nt2,
      tk_pscl_no: data.tk_pscl_no,
      tk_pscl_co: data.tk_pscl_co,
      stt: typeof data.stt === 'string' ? parseInt(data.stt) : data.stt,
      ra_ndec: typeof data.ra_ndec === 'string' ? parseInt(data.ra_ndec) : data.ra_ndec,
      ra_1: data.ra_1 || null,
      ra_2: data.ra_2 || null,
      ra_3: data.ra_3 || null,
      ra_4: data.ra_4 || null,
      ra_5: data.ra_5 || null,
      ra_12: data.ra_12 || null,
      ra_22: data.ra_22 || null,
      ra_32: data.ra_32 || null,
      ra_42: data.ra_42 || null,
      ra_52: data.ra_52 || null,
      cach_doc: data.ten_nt || data.cach_doc,
      status: String(data.status),
      entity_model: entity?.uuid
    };
  }

  /**
   * Get a currency by its UUID
   */
  const getCurrencyById = async (uuid: string): Promise<NgoaiTe | null> => {
    if (!entity?.slug) return null;

    try {
      const response = await api.get<NgoaiTe>(`/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching currency with UUID ${uuid}:`, error);
      return null;
    }
  };

  /**
   * Get a currency by its code
   */
  const getCurrencyByCode = async (code: string): Promise<NgoaiTe | null> => {
    if (!entity?.slug || !code) return null;

    try {
      const response = await api.get<NgoaiTeResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/`, {
        params: { ma_nt: code }
      });

      const results = response.data.results || [];
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error(`Error fetching currency with code ${code}:`, error);
      return null;
    }
  };

  /**
   * Add a new currency
   */
  const addCurrency = async (newCurrency: NgoaiTeInput): Promise<NgoaiTe> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload =
        typeof newCurrency === 'object' && 'ma_nt' in newCurrency && typeof newCurrency.stt === 'string'
          ? prepareDataForApi(newCurrency)
          : newCurrency;

      const response = await api.post<NgoaiTe>(`/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/`, payload);
      const addedCurrency = response.data;

      // Update local state
      setCurrencies(prev => [...prev, addedCurrency]);
      return addedCurrency;
    } catch (error) {
      console.error('Error adding currency:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing currency
   */
  const updateCurrency = async (uuid: string, updatedCurrency: NgoaiTeInput): Promise<NgoaiTe> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      // Prepare data before sending to API if needed
      const payload =
        typeof updatedCurrency === 'object' && 'ma_nt' in updatedCurrency && typeof updatedCurrency.stt === 'string'
          ? prepareDataForApi(updatedCurrency)
          : updatedCurrency;

      const response = await api.patch<NgoaiTe>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/${uuid}/`,
        payload
      );
      const updatedData = response.data;

      // Update local state
      setCurrencies(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating currency:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a currency
   */
  const deleteCurrency = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.NGOAI_TE}/${uuid}/`);

      // Update local state
      setCurrencies(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting currency:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Load currencies on component mount
  useEffect(() => {
    if (entity?.slug) {
      fetchCurrencies();
    }
  }, [entity?.slug, fetchCurrencies]);

  return {
    currencies,
    isLoading,
    addCurrency,
    updateCurrency,
    deleteCurrency,
    refreshCurrencies: fetchCurrencies,
    getCurrencyById,
    getCurrencyByCode
  };
};
